import type {
  ResultEqualize,
  ResultInnerDetail,
  ResultScoreDetails,
  ResultSort,
} from '@empathybroker/tsclient-play/dist/models/search/result'
import type { Result } from '@empathyco/x-types'
import { ResultSortCriteriaIdEnum } from '@empathybroker/tsclient-play/dist/enums/search/debug/result/result-sort-criteria-id.enum'
import { QueryRankingDirectionTypeEnum } from '@empathybroker/tsclient-play/dist/enums/search/debug/result/score-criteria-detail-type.enum'

export class ExplainCSVExportRow {
  public position: number
  protected readonly result: Result

  public constructor(result: Result, attrs: { position: number }) {
    this.result = result
    this.position = attrs.position
  }

  public get id(): string | number {
    return this.result.id
  }

  public get name(): string | undefined {
    return this.result.name
  }

  public get brand(): string | undefined {
    return this.result.brand
  }

  public get cfSize(): string | undefined {
    return this.result.cfSize
  }

  public get cfDepartments(): string[] {
    return this.result.cfDepartments ?? []
  }

  public get cfCommodities(): string[] {
    return this.result.cfCommodities ?? []
  }

  public get cfSubCommodities(): string[] {
    return this.result.cfSubCommodities ?? []
  }

  public get primaryDepartments(): string[] {
    return this.result.primaryDepartments ?? []
  }

  public get recapDepartments(): string[] {
    return this.result.recapDepartments ?? []
  }

  public get ftDepartments(): string[] {
    return this.result.ftDepartments ?? []
  }

  public get ftCommodities(): string[] {
    return this.result.ftCommodities ?? []
  }

  public get ftSubCommodities(): string[] {
    return this.result.ftSubCommodities ?? []
  }

  public get totalScore(): number | null {
    return this.scoreSortCriteria()?.value ?? null
  }

  public get semanticSearchScore(): number | null {
    return this.computeScore(
      this.scoreSortCriteriaDetails()?.semanticSearchInfo?.details?.sdi?.details,
    )
  }

  public get topProductScore(): number | null {
    return this.computeScore(
      this.scoreSortCriteriaDetails()?.wisdomOfTheCrowd?.details?.topProduct?.details,
    )
  }

  public get topAttributesScore(): number | null {
    return this.computeScore(
      this.scoreSortCriteriaDetails()?.wisdomOfTheCrowd?.details?.topAttributes?.details,
    )
  }

  public get termPositionScore(): number | null {
    return this.computeScore(this.scoreSortCriteriaDetails()?.termPosition?.details)
  }

  public get customizationScore(): number | null {
    return this.computeScore(this.scoreSortCriteriaDetails()?.personalization?.details)
  }

  public get textualMatchTotalScore(): number | null {
    return this.computeScore(this.equalizeDetails()?.textualMatch?.details)
  }

  textualMatchScore(textualMatchKey: string): number | null {
    return this.findTextualMatchDetailByKey(textualMatchKey)?.value ?? null
  }

  public get gbrBoostScore(): number | null {
    return this.computeInnerDetailBoostScore(this.equalizeDetails()?.globalBusinessRules?.details)
  }

  public get gbrBuriesScore(): number | null {
    return this.computeInnerDetailBuryScore(this.equalizeDetails()?.globalBusinessRules?.details)
  }

  public get functionScore(): number | null {
    return this.computeScore(this.equalizeDetails()?.functionScore?.details)
  }

  public get productBoostScore(): number | null {
    return this.computeInnerDetailBoostScore(
      this.scoreSortCriteriaDetails()?.queryRanking?.details?.products?.details,
    )
  }

  public get attributeBoostScore(): number | null {
    return this.computeInnerDetailBoostScore(
      this.scoreSortCriteriaDetails()?.queryRanking?.details?.attributes?.details,
    )
  }

  public get productBuryScore(): number | null {
    return this.computeInnerDetailBuryScore(
      this.scoreSortCriteriaDetails()?.queryRanking?.details?.products?.details,
    )
  }

  public get attributeBuryScore(): number | null {
    return this.computeInnerDetailBuryScore(
      this.scoreSortCriteriaDetails()?.queryRanking?.details?.attributes?.details,
    )
  }

  public get tieBreaker(): number | null {
    return this.otherSortCriteria()?.value ?? null
  }

  private computeScore(details: ResultInnerDetail[] | undefined, isBury = false): number | null {
    if (!details?.length) {
      return null
    }

    if (isBury) {
      return details.reduce((score: number, cur: ResultInnerDetail) => score * cur.value, 1)
    }

    return details.reduce((score: number, cur: ResultInnerDetail) => score + cur.value, 0)
  }

  private computeInnerDetailBuryScore(
    innerDetails: ResultInnerDetail[] | undefined,
  ): number | null {
    return this.computeInnerDetailScore(innerDetails, QueryRankingDirectionTypeEnum.BURY)
  }

  private computeInnerDetailBoostScore(
    innerDetails: ResultInnerDetail[] | undefined,
  ): number | null {
    return this.computeInnerDetailScore(innerDetails, QueryRankingDirectionTypeEnum.BOOST)
  }

  private computeInnerDetailScore(
    innerDetails: ResultInnerDetail[] | undefined,
    queryRankingDirectionType: QueryRankingDirectionTypeEnum,
  ): number | null {
    if (innerDetails?.length) {
      return this.computeScore(
        innerDetails.filter(detail => detail.type === queryRankingDirectionType),
        queryRankingDirectionType === QueryRankingDirectionTypeEnum.BURY,
      )
    }
    return null
  }

  private scoreSortCriteria(): ResultSort | undefined {
    return this.result.debug?.sort.find(item => item.id === ResultSortCriteriaIdEnum.SCORE)
  }

  private otherSortCriteria(): ResultSort | undefined {
    return this.result.debug?.sort.find(item => item.id !== ResultSortCriteriaIdEnum.SCORE)
  }

  private scoreSortCriteriaDetails(): ResultScoreDetails | undefined {
    return this.scoreSortCriteria()?.details
  }

  private equalizeDetails(): Exclude<ResultEqualize['details'], []> | undefined {
    const details = this.scoreSortCriteriaDetails()
    if (details && !Array.isArray(details?.equalize?.details)) {
      return details?.equalize?.details
    }
  }

  textualMatchDetails(): ResultInnerDetail[] {
    return this.equalizeDetails()?.textualMatch?.details ?? []
  }

  private findTextualMatchDetailByKey(textualMatchKey: string): ResultInnerDetail | undefined {
    const textualMatchDetails = this.textualMatchDetails()
    return textualMatchDetails.find(
      textualMatchDetailObj => textualMatchDetailObj.fieldName === textualMatchKey,
    )
  }
}
